import {
  ISendMessagePayload,
  SEND_MESSAGE_TYPE,
} from "@home-health-notify/domain";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import { IconButton, Slide, Tooltip } from "@mui/material";
import { TransitionProps } from "@mui/material/transitions";
import {
  ApiErrorHandler,
  useFetchQuickReplies,
  useTranslation,
} from "hhn-commons";
import { useSnackbar } from "notistack";
import React, { useEffect } from "react";
import { RESPONSIVE_DRAWER } from "../../../common/Common.style";
import { Dialog } from "../../../common/modal/Dialog";
import ProgressLoader from "../../../loader/ProgressLoader";
import {
  EmptyQuickReplies,
  PriorityMessageLabel,
  QuickReplyActions,
  QuickReplyModalBody,
  QuickReplyModalHeader,
  QuickReplyTitle,
  QuickReplyWrapper,
} from "./QuickReply.style";
import QuickReplyAdd from "./QuickReplyAdd";
import QuickReplyList from "./QuickReplyList";
import { PriorityChip } from "../../MessagePanel.style";
import WarningRoundedIcon from "@mui/icons-material/WarningRounded";

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

interface QuickReplyModalProps {
  open: boolean;
  closeModal: () => void;
  sendMessage: (
    payload: ISendMessagePayload,
    sendMessageType: SEND_MESSAGE_TYPE,
  ) => void;
  isPriority: boolean;
  setPriority: (isPriority: boolean) => void;
}

const QuickReplyModal = ({
  open,
  closeModal,
  isPriority,
  sendMessage,
  setPriority,
}: QuickReplyModalProps) => {
  const { t } = useTranslation();
  const tNs = (key: string) => t(key, { ns: "quickReply" });
  const {
    quickReplies,
    fetchQuickReplies,
    isLoading,
    addQuickReply,
    deleteQuickReply,
  } = useFetchQuickReplies();
  const [isManageMode, setIsManageMode] = React.useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const loadQuickReplies = async () => {
    try {
      await fetchQuickReplies();
    } catch (e) {
      ApiErrorHandler(e, enqueueSnackbar, tNs("fetchError"));
    }
  };

  useEffect(() => {
    if (open) {
      loadQuickReplies();
    } else {
      setIsManageMode(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const onManage = () => {
    setIsManageMode(!isManageMode);
  };

  const onSend = (message: string) => {
    let sendChatMessage: ISendMessagePayload = {
      body: message,
    };

    if (sendChatMessage) {
      sendMessage(
        sendChatMessage,
        isPriority ? SEND_MESSAGE_TYPE.PRIORITY : SEND_MESSAGE_TYPE.NORMAL,
      );
      setPriority(false);
      closeModal();
    }
  };

  const onAdd = async (message: string) => {
    try {
      await addQuickReply(message);
      return true;
    } catch (e) {
      ApiErrorHandler(e, enqueueSnackbar, tNs("addError"));
      return false;
    }
  };

  const renderManageRepliesButton = () => (
    <Tooltip title={tNs("manageTooltip")}>
      <IconButton onClick={onManage}>
        {isManageMode ? <CheckIcon /> : <EditIcon />}
      </IconButton>
    </Tooltip>
  );

  const onDelete = async (index: number) => {
    try {
      await deleteQuickReply(index);
    } catch (e) {
      ApiErrorHandler(e, enqueueSnackbar, tNs("deleteError"));
    }
  };

  return (
    <Dialog
      open={open}
      keepMounted
      onClose={closeModal}
      slots={{ transition: Transition }}
      slotProps={{
        paper: {
          sx: {
            position: "absolute",
            bottom: 0,
            margin: 0,
            width: "54%",
            maxWidth: "100%",
            paddingBottom: "40px",
            [`@media (max-width: ${RESPONSIVE_DRAWER.WIDE_BP - 1}px)`]: {
              width: "70%",
            },
          },
        },
      }}
    >
      <>
        <QuickReplyModalHeader>
          <QuickReplyTitle>{tNs("quickReplyTitle")}</QuickReplyTitle>
          {isPriority && (
            <PriorityMessageLabel>
              <PriorityChip label={tNs("priorityLabel")} color="error" />
            </PriorityMessageLabel>
          )}

          <QuickReplyActions>
            <WarningRoundedIcon
              color={isPriority ? "error" : "disabled"}
              fontSize="large"
              onClick={() => setPriority(!isPriority)}
            />
            {renderManageRepliesButton()}
            <IconButton onClick={closeModal} data-testid="close-button">
              <CloseIcon />
            </IconButton>
          </QuickReplyActions>
        </QuickReplyModalHeader>
        <QuickReplyModalBody>
          {quickReplies.length === 0 && isLoading ? (
            <ProgressLoader style={{ alignSelf: "center" }} />
          ) : quickReplies.length === 0 ? (
            <QuickReplyWrapper>
              {isManageMode && (
                <QuickReplyAdd
                  onAdd={onAdd}
                  isLoading={isLoading}
                  quickRepliesCount={quickReplies.length}
                />
              )}
              <EmptyQuickReplies data-testid="empty-quick-replies">
                {tNs("emptyReplyListMessage")}
              </EmptyQuickReplies>
            </QuickReplyWrapper>
          ) : (
            <QuickReplyWrapper>
              {isManageMode && (
                <QuickReplyAdd
                  onAdd={onAdd}
                  isLoading={isLoading}
                  quickRepliesCount={quickReplies.length}
                />
              )}
              <QuickReplyList
                quickReplies={quickReplies}
                onSend={onSend}
                isManageMode={isManageMode}
                onDelete={onDelete}
              />
            </QuickReplyWrapper>
          )}
        </QuickReplyModalBody>
      </>
    </Dialog>
  );
};

export default QuickReplyModal;
